import { initializeApp } from 'firebase/app';
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from 'firebase/auth';
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  collection,
  query,
  where,
  orderBy,
  getDocs,
  serverTimestamp,
} from 'firebase/firestore';

// ─── CONFIG ────────────────────────────────────────────────────────────────
// Sostituisci con i valori del tuo progetto Firebase
// (Settings → General → Your apps → Firebase SDK snippet → Config)
const firebaseConfig = {
  apiKey: "AIzaSyBuD4UwwE1ya8bGO-9t5r_9oOhsPUJ_SjE",
  authDomain: "just-salento.firebaseapp.com",
  projectId: "just-salento",
  storageBucket: "just-salento.appspot.com",
  messagingSenderId: "386735750952",
  appId: "1:386735750952:web:6dcff4bebf82e2bce21a1d"
};

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);

// ─── AUTH ───────────────────────────────────────────────────────────────────

/**
 * Registra un nuovo gestore di lido.
 * onboardingCompleto = false finché non configura servizi + layout
 */
export async function registerBeachAdmin({ email, password, nomeLido, indirizzo, telefono }) {
  const credential = await createUserWithEmailAndPassword(auth, email, password);
  const uid = credential.user.uid;

  await setDoc(doc(db, 'admins', uid), {
    email,
    beachCode: uid,
    createdAt: serverTimestamp(),
    approved: true,
    onboardingCompleto: false,
  });

  await setDoc(doc(db, 'lidi', uid), {
    codice: uid,
    nome: nomeLido,
    indirizzo,
    telefono,
    email,
    servizi: [],
    gridCols: 10,
    gridRows: 5,
    galleria_fotografica: [],
    createdAt: serverTimestamp(),
  });

  return uid;
}

export async function loginAdmin(email, password) {
  const credential = await signInWithEmailAndPassword(auth, email, password);
  return credential.user;
}

export async function logoutAdmin() {
  await signOut(auth);
}

export function onAuthChange(callback) {
  return onAuthStateChanged(auth, callback);
}

// ─── ADMIN / LIDO ───────────────────────────────────────────────────────────

export async function getAdminData(uid) {
  const snap = await getDoc(doc(db, 'admins', uid));
  return snap.exists() ? { uid, ...snap.data() } : null;
}

export async function getBeach(beachCode) {
  const snap = await getDoc(doc(db, 'lidi', beachCode));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function updateBeach(beachCode, data) {
  await updateDoc(doc(db, 'lidi', beachCode), data);
}

export async function completeOnboarding(uid) {
  await updateDoc(doc(db, 'admins', uid), { onboardingCompleto: true });
}

// ─── SERVIZI ────────────────────────────────────────────────────────────────
/**
 * Struttura servizio:
 * {
 *   id: string,          // es. "s1"
 *   label: string,       // es. "Ombrellone", "Gazebo", "Lettino"
 *   haFila: boolean,     // se true il prezzo varia per riga
 *   colore: string,      // hex — usato nella griglia
 *   icona: string,       // emoji — usata nella palette
 * }
 * Salvati come array in lidi/{beachCode}.servizi
 */
export async function saveServizi(beachCode, servizi) {
  await updateDoc(doc(db, 'lidi', beachCode), { servizi });
}

// ─── LAYOUT SPIAGGIA ────────────────────────────────────────────────────────
/**
 * Il layout template viene salvato nel documento del lido.
 * Struttura cella:
 * {
 *   col: number, row: number,
 *   type: 'vuoto' | servizioId,
 *   id: string,      // es. "A1", "B3" — auto-generato
 * }
 * Quando si pubblica, il layout viene applicato alle date della stagione
 * come disponibilita/{dateStr} con status 'free' su ogni cella non vuota.
 */
export async function saveLayoutTemplate(beachCode, { gridCols, gridRows, celle }) {
  await updateDoc(doc(db, 'lidi', beachCode), { gridCols, gridRows, layoutTemplate: celle });
}

/**
 * Applica il layout template a un range di date.
 * Per ogni data crea/sovrascrive disponibilita/{dateStr}.
 */
export async function pubblicaLayout(beachCode, { celle, dal, al }) {
  const start = new Date(dal);
  const end = new Date(al);
  const current = new Date(start);

  while (current <= end) {
    const dateStr = current.toISOString().split('T')[0];
    const layout = celle
      .filter((c) => c.type !== 'vuoto')
      .map((c) => ({ ...c, status: 'free' }));

    await setDoc(doc(db, `lidi/${beachCode}/disponibilita`, dateStr), {
      dateStr,
      layout,
      updatedAt: serverTimestamp(),
    });

    current.setDate(current.getDate() + 1);
  }
}

export async function getLayoutByDate(beachCode, dateStr) {
  const snap = await getDoc(doc(db, `lidi/${beachCode}/disponibilita`, dateStr));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function getAllLayouts(beachCode) {
  const snap = await getDocs(collection(db, `lidi/${beachCode}/disponibilita`));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// ─── TARIFFE ────────────────────────────────────────────────────────────────
/**
 * Struttura tariffa:
 * {
 *   id: string,
 *   nome: string,         // es. "Ferragosto", "Bassa stagione"
 *   dal: string,          // "YYYY-MM-DD"
 *   al: string,           // "YYYY-MM-DD"
 *   priorita: number,     // in caso di sovrapposizione vince la priorità più alta
 *   prezzi: {
 *     [servizioId]: number,               // per servizi senza fila
 *     [`${servizioId}_r${rowIndex}`]: number,  // per servizi con fila (r1 = prima fila)
 *   }
 * }
 */
export async function getTariffe(beachCode) {
  const snap = await getDocs(
    query(collection(db, `lidi/${beachCode}/tariffe`), orderBy('dal', 'asc'))
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function saveTariffa(beachCode, tariffa) {
  if (tariffa.id) {
    const { id, ...data } = tariffa;
    await updateDoc(doc(db, `lidi/${beachCode}/tariffe`, id), data);
    return id;
  } else {
    const ref = await addDoc(collection(db, `lidi/${beachCode}/tariffe`), tariffa);
    return ref.id;
  }
}

export async function deleteTariffa(beachCode, tariffaId) {
  await deleteDoc(doc(db, `lidi/${beachCode}/tariffe`, tariffaId));
}

/**
 * Calcola il prezzo di un elemento del layout per una data specifica.
 * Cerca la tariffa attiva con priorità più alta.
 * Se nessuna tariffa copre la data, restituisce null.
 */
export function calcolaPrezzo(tariffe, dateStr, servizioId, rowIndex, haFila) {
  const attive = tariffe
    .filter((t) => t.dal <= dateStr && t.al >= dateStr)
    .sort((a, b) => (b.priorita || 0) - (a.priorita || 0));

  if (!attive.length) return null;

  const tariffa = attive[0];
  const key = haFila ? `${servizioId}_r${rowIndex}` : servizioId;
  return tariffa.prezzi?.[key] ?? null;
}

// ─── PRENOTAZIONI ───────────────────────────────────────────────────────────

export async function getOrdersByBeach(beachCode) {
  const q = query(
    collection(db, 'orders'),
    where('beachCode', '==', beachCode),
    orderBy('createdAt', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function getOrdersByBeachAndDate(beachCode, date) {
  const q = query(
    collection(db, 'orders'),
    where('beachCode', '==', beachCode),
    where('selectedDate', 'array-contains', date)
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function updateOrderStatus(orderId, status) {
  await updateDoc(doc(db, 'orders', orderId), { status });
}

export async function cancelOrder(orderId) {
  await updateOrderStatus(orderId, 'cancelled');
}

export async function createOrderManual({
  beachCode, servizioId, servizioLabel, celle,
  totalPrice, selectedDate, guestName, notes,
}) {
  const ref = await addDoc(collection(db, 'orders'), {
    beachCode,
    userId: 'admin_manual',
    guestName: guestName || '',
    servizioId,
    servizioLabel,
    celle,          // array di {col, row, id} prenotate
    totalPrice,
    selectedDate,
    notes: notes || '',
    status: 'confirmed',
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

// ─── UTENTI ─────────────────────────────────────────────────────────────────

export async function getUserData(uid) {
  const snap = await getDoc(doc(db, 'users', uid));
  return snap.exists() ? { uid, ...snap.data() } : null;
}

// ─── PRENOTAZIONE MANUALE / BLOCCO ──────────────────────────────────────────

/**
 * Calcola l'array di dateStr a partire dal tipo di prenotazione
 */
export function expandDates({ dateType, selectedDate, dateRange, dates, seasonalDays }) {
  if (dateType === 'single') {
    return [selectedDate];
  }

  if (dateType === 'multiple') {
    return dates || [];
  }

  if (dateType === 'range' || dateType === 'seasonal') {
    const result = [];
    const start = new Date(dateRange.dal);
    const end = new Date(dateRange.al);
    const cur = new Date(start);
    while (cur <= end) {
      if (dateType === 'range' || seasonalDays.includes(cur.getDay())) {
        result.push(cur.toISOString().split('T')[0]);
      }
      cur.setDate(cur.getDate() + 1);
    }
    return result;
  }

  return [];
}

/**
 * Crea una prenotazione manuale e aggiorna lo status delle celle
 * per ogni data coinvolta
 */
export async function createBooking(beachCode, {
  celle, guestName, guestPhone, notes,
  dateType, selectedDate, dateRange, dates, seasonalDays,
  totalPrice, status = 'confirmed',
}) {
  const allDates = expandDates({ dateType, selectedDate, dateRange, dates, seasonalDays });

  // 1. Crea il documento ordine
  const ref = await addDoc(collection(db, 'orders'), {
    beachCode,
    celle,
    guestName: guestName || '',
    guestPhone: guestPhone || '',
    notes: notes || '',
    dateType,
    selectedDate: allDates,
    dateRange: dateRange || null,
    seasonalDays: seasonalDays || null,
    totalPrice: totalPrice || 0,
    status,
    source: 'admin_manual',
    createdAt: serverTimestamp(),
  });

  // 2. Aggiorna lo status nelle celle di disponibilità per ogni data
  const newStatus = status === 'confirmed' ? 'occupied' : 'reserved';
  const celleIds = celle.map(c => c.id);

  await Promise.all(allDates.map(async (dateStr) => {
    const snap = await getDoc(doc(db, `lidi/${beachCode}/disponibilita`, dateStr));
    if (!snap.exists()) return;
    const layout = snap.data().layout || [];
    const updated = layout.map(c =>
      celleIds.includes(c.id) ? { ...c, status: newStatus } : c
    );
    await updateDoc(doc(db, `lidi/${beachCode}/disponibilita`, dateStr), { layout: updated });
  }));

  return ref.id;
}

/**
 * Blocca un posto (manutenzione, riservato, ecc.)
 */
export async function blockSpot(beachCode, params) {
  return createBooking(beachCode, { ...params, status: 'blocked' });
}

/**
 * Cancella una prenotazione e ripristina le celle come free
 */
export async function cancelBookingAndRelease(beachCode, orderId, celle, allDates) {
  await updateDoc(doc(db, 'orders', orderId), { status: 'cancelled' });
  const celleIds = celle.map(c => c.id);
  await Promise.all(allDates.map(async (dateStr) => {
    const snap = await getDoc(doc(db, `lidi/${beachCode}/disponibilita`, dateStr));
    if (!snap.exists()) return;
    const layout = snap.data().layout || [];
    const updated = layout.map(c =>
      celleIds.includes(c.id) ? { ...c, status: 'free' } : c
    );
    await updateDoc(doc(db, `lidi/${beachCode}/disponibilita`, dateStr), { layout: updated });
  }));
}